import {
    PaginationContent,
    PaginationEllipsis,
    PaginationFirst,
    PaginationItem,
    PaginationLast,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    Pagination as ShadcnPagination,
} from "@/components/shadcn/pagination";
import type { Link } from "@/i18n/navigation";
import { getPaginationRange } from "@/lib/pagination";
import { cn } from "cn";
import type { ComponentProps, HTMLAttributes, MouseEvent } from "react";

export type PageHref = ComponentProps<typeof Link>["href"];

interface PaginationBaseProps {
    pageCount: number;
    currentPage: number;
    /** Pages kept on each side of the current one; lower it in tight columns. */
    siblingCount?: number;
    /** The « » chevrons are droppable where space is tight; the 1 / last links remain. */
    showFirstLast?: boolean;
}

/**
 * Either the pages live in the URL (`hrefForPage`, which then requires a
 * `pathname`) or they are held in client state (`onPageChange`). The union
 * keeps `pathname` from being forgotten: without it the locale-aware Link
 * serializes a query-only href to `/undefined?page=2`.
 */
type PaginationProps = PaginationBaseProps &
    (
        | {
              /** Where a page lives in the URL. */
              hrefForPage: (page: number) => PageHref;
              /**
               * The current route, without the locale prefix. Callers build
               * query-only hrefs ({ query, hash }) and the Link needs a pathname
               * to prefix — this component renders on the server too, so it
               * cannot reach for usePathname() itself.
               */
              pathname: string;
              /** Also reports the page the user picked, on top of navigating. */
              onPageChange?: (page: number) => void;
          }
        | {
              hrefForPage?: undefined;
              pathname?: undefined;
              /**
               * Reports the page the user picked; the caller decides what that
               * means. Without `hrefForPage` the click is only a report — the
               * anchor never navigates.
               */
              onPageChange: (page: number) => void;
          }
    );

export default function Pagination({
    pageCount,
    currentPage,
    onPageChange,
    hrefForPage,
    pathname,
    siblingCount,
    showFirstLast = true,
    className,
}: HTMLAttributes<HTMLDivElement> & PaginationProps) {
    const pages = getPaginationRange(currentPage, pageCount, { siblingCount });
    // Callers build query-only hrefs ({ query, hash }); the locale-aware Link
    // needs a pathname to prefix, so fill in the one the caller resolved.
    const withPathname = (href: PageHref): PageHref =>
        typeof href === "object" && !href.pathname
            ? { ...href, pathname }
            : href;
    const hasPrevPage = currentPage > 1;
    const hasNextPage = currentPage < pageCount;

    // The onClick is attached only when needed: from a server component this
    // renders with hrefForPage alone, and a function prop would be illegal to
    // pass across the server/client boundary to the link.
    const linkToPage = (page: number) => ({
        href: withPathname(hrefForPage?.(page) ?? "#!"),
        ...(onPageChange || !hrefForPage
            ? {
                  onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                      if (!hrefForPage) {
                          event.preventDefault();
                      }
                      onPageChange?.(page);
                  },
              }
            : {}),
    });

    // Unreachable pages keep an inert href so crawlers never see a page=0 or a
    // self-link, on top of the pointer-events guard that stops the click.
    const linkToPageIf = (enabled: boolean, page: number) =>
        enabled ? linkToPage(page) : { href: "#!" };

    return (
        <ShadcnPagination className={className}>
            <PaginationContent>
                {showFirstLast && (
                    <PaginationItem className="max-sm:hidden">
                        <PaginationFirst
                            {...linkToPageIf(hasPrevPage, 1)}
                            aria-disabled={!hasPrevPage}
                            className={cn(
                                !hasPrevPage &&
                                    "pointer-events-none opacity-50",
                            )}
                        />
                    </PaginationItem>
                )}
                <PaginationItem>
                    <PaginationPrevious
                        {...linkToPageIf(hasPrevPage, currentPage - 1)}
                        aria-disabled={!hasPrevPage}
                        className={cn(
                            !hasPrevPage && "pointer-events-none opacity-50",
                        )}
                    />
                </PaginationItem>
                {pages.map((item, index) =>
                    item === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={item}>
                            <PaginationLink
                                {...linkToPageIf(currentPage !== item, item)}
                                isActive={currentPage === item}
                                className={cn(
                                    currentPage === item &&
                                        "pointer-events-none",
                                )}
                            >
                                {item}
                            </PaginationLink>
                        </PaginationItem>
                    ),
                )}
                <PaginationItem>
                    <PaginationNext
                        {...linkToPageIf(hasNextPage, currentPage + 1)}
                        aria-disabled={!hasNextPage}
                        className={cn(
                            !hasNextPage && "pointer-events-none opacity-50",
                        )}
                    />
                </PaginationItem>
                {showFirstLast && (
                    <PaginationItem className="max-sm:hidden">
                        <PaginationLast
                            {...linkToPageIf(hasNextPage, pageCount)}
                            aria-disabled={!hasNextPage}
                            className={cn(
                                !hasNextPage &&
                                    "pointer-events-none opacity-50",
                            )}
                        />
                    </PaginationItem>
                )}
            </PaginationContent>
        </ShadcnPagination>
    );
}
